/**
 * FinalSection - Final phase section for scroll wizard
 */
import React, { useState, useEffect } from 'react';
import { useTournament } from '../../contexts/TournamentContext';
import MatchScore from '../../models/MatchScore';
import './FinalSection.css';

const FinalSection = () => {
  const { players, config, finalMatch, setFinalMatch, setTournamentFinished } = useTournament();
  const [, setRefresh] = useState(0);
  const [formula, setFormula] = useState('position');

  const createMatchScore = () => new MatchScore(
    config.setsToWin || 1,
    config.gamesPerSet !== undefined ? config.gamesPerSet : null,
    config.tiebreakPoints || 7,
    config.allowDraws !== undefined ? config.allowDraws : true
  );

  const buildTeams = (sorted, selectedFormula) => {
    if (selectedFormula === 'top4') {
      if (sorted.length < 4) return null;
      const top4 = sorted.slice(0, 4);
      const left = top4.filter(p => p.position === 'left');
      const right = top4.filter(p => p.position === 'right');
      if (left.length === 2 && right.length === 2) {
        // 2G + 2D dans le top 4 : appliquer la formule position
        return {
          team1: [left[0].id, right[1].id],
          team2: [right[0].id, left[1].id]
        };
      }
      // Sinon : Rang1+Rang4 vs Rang2+Rang3
      return {
        team1: [top4[0].id, top4[3].id],
        team2: [top4[1].id, top4[2].id]
      };
    }
    // formula 'position'
    const left = sorted.filter(p => p.position === 'left');
    const right = sorted.filter(p => p.position === 'right');
    if (left.length < 2 || right.length < 2) return null;
    return {
      team1: [left[0].id, right[1].id],
      team2: [right[0].id, left[1].id]
    };
  };

  // Initialize finalMatch once players have actual stats
  useEffect(() => {
    if (!finalMatch && players.length >= 4 && players.some(p => p.played > 0)) {
      const sorted = [...players].sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        if (b.setsWon !== a.setsWon) return b.setsWon - a.setsWon;
        if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
        return a.gamesLost - b.gamesLost;
      });
      const teams = buildTeams(sorted, formula);
      if (teams) {
        setFinalMatch({
          ...teams,
          result: null,
          validated: false,
          detailedScore: createMatchScore(),
          playerResults: null
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, formula, finalMatch]);

  const handleFormulaChange = (newFormula) => {
    setFormula(newFormula);
    setFinalMatch(null);
  };


  // Update score input
  const handleScoreInput = (setIndex, team, value, isTiebreak = false) => {
    if (!finalMatch || finalMatch.validated) return;
    const updatedMatch = { ...finalMatch };
    const parsedValue = parseInt(value) || 0;

    if (isTiebreak) {
      if (!updatedMatch.detailedScore.sets[setIndex]) {
        updatedMatch.detailedScore.sets[setIndex] = { team1: 0, team2: 0, tiebreak: null };
      }
      if (!updatedMatch.detailedScore.sets[setIndex].tiebreak) {
        updatedMatch.detailedScore.sets[setIndex].tiebreak = { team1: 0, team2: 0 };
      }
      updatedMatch.detailedScore.sets[setIndex].tiebreak[team === 1 ? 'team1' : 'team2'] = parsedValue;
    } else {
      const team1Input = team === 1 ? parsedValue : (updatedMatch.detailedScore.sets[setIndex]?.team1 || 0);
      const team2Input = team === 2 ? parsedValue : (updatedMatch.detailedScore.sets[setIndex]?.team2 || 0);
      const maxGames = config.gamesPerSet || 6;
      const needsTiebreak =
        (team1Input === maxGames + 1 && team2Input === maxGames) ||
        (team2Input === maxGames + 1 && team1Input === maxGames);
      const tiebreak = needsTiebreak
        ? (updatedMatch.detailedScore.sets[setIndex]?.tiebreak || { team1: 0, team2: 0 })
        : null;
      updatedMatch.detailedScore.setSetScore(setIndex, team1Input, team2Input, tiebreak);
    }

    updatedMatch.detailedScore.calculate();
    if (updatedMatch.detailedScore.isComplete()) {
      updatedMatch.result = updatedMatch.detailedScore.isDraw() ? 0
        : updatedMatch.detailedScore.winner === 0 ? 1 : 2;
    } else {
      updatedMatch.result = null;
    }

    setFinalMatch(updatedMatch);
    setRefresh(prev => prev + 1);
  };

  // Validate final match
  const validateFinal = () => {
    if (!finalMatch || !finalMatch.detailedScore?.isComplete()) return;
    const team1Stats = finalMatch.detailedScore.stats.team1;
    const team2Stats = finalMatch.detailedScore.stats.team2;
    const playerResults = {};
    finalMatch.team1.forEach(playerId => {
      playerResults[playerId] = { ...team1Stats };
    });
    finalMatch.team2.forEach(playerId => {
      playerResults[playerId] = { ...team2Stats };
    });
    setFinalMatch({ ...finalMatch, validated: true, playerResults });
    setTournamentFinished(true);
    setRefresh(prev => prev + 1);
  };

  // Unvalidate to edit
  const unvalidateFinal = () => {
    if (!finalMatch) return;
    const { playerResults, ...rest } = finalMatch;
    setFinalMatch({ ...rest, validated: false, result: null });
    setTournamentFinished(false);
    setRefresh(prev => prev + 1);
  };

  const getPlayerById = (id) => players.find(p => p.id === id);

  if (players.length < 4) {
    return (
      <div className="section-inner">
        <div className="section-header">
          <h1><i className="bi bi-flag-fill me-3"></i>Phase finale</h1>
        </div>
        <div className="section-body">
          <div className="alert alert-warning">Pas assez de joueurs pour organiser une phase finale.</div>
        </div>
      </div>
    );
  }

  const isComplete = finalMatch?.detailedScore?.isComplete() || false;
  const isValidated = finalMatch?.validated || false;
  const winner = finalMatch?.result ?? null;

  const sortedForTop4 = [...players].sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.setsWon !== a.setsWon) return b.setsWon - a.setsWon;
    if (b.gamesWon !== a.gamesWon) return b.gamesWon - a.gamesWon;
    return a.gamesLost - b.gamesLost;
  }).slice(0, 4);
  const top4LeftCount = sortedForTop4.filter(p => p.position === 'left').length;
  const top4RightCount = sortedForTop4.filter(p => p.position === 'right').length;
  const showTop4Button = top4LeftCount > 2 || top4RightCount > 2;

  return (
    <div className="section-inner">
      <div className="section-header">
        <h1>
          <i className="bi bi-flag-fill me-3"></i>
          Phase finale
        </h1>
      </div>

      <div className="section-body">

        {/* Special case warning */}
        {showTop4Button && !isValidated && (
          <p className="final-formula-desc text-center mb-3">
            <i className="bi bi-exclamation-triangle-fill me-1 text-warning"></i>
            Cas spécial : il y a 3 {top4RightCount >= 3 ? 'Droitiers' : 'Gauchers'} dans le top 4.
            Choisissez la finale : les 4 premiers jouent, ou les meilleurs Droitiers &amp; Gauchers.
          </p>
        )}

        {/* Formula selector */}
        {!isValidated && (
          <div className="final-formula-selector mb-4">
            {showTop4Button && (
              <button
                type="button"
                className={`btn btn-sm final-formula-btn ${formula === 'position' ? 'active' : ''}`}
                onClick={() => handleFormulaChange('position')}
              >
                <i className="bi bi-person-fill me-1"></i>G / D
              </button>
            )}
            {showTop4Button && (
              <button
                type="button"
                className={`btn btn-sm final-formula-btn ${formula === 'top4' ? 'active' : ''}`}
                onClick={() => handleFormulaChange('top4')}
              >
                <i className="bi bi-trophy-fill me-1"></i>Top 4
              </button>
            )}
            {!showTop4Button && (
              <button
                type="button"
                className="btn btn-sm final-formula-btn"
                onClick={() => setFinalMatch(null)}
              >
                <i className="bi bi-arrow-repeat me-1"></i>Recalculer
              </button>
            )}
          </div>
        )}

        {/* Formula info */}
        <p className="final-formula-desc text-center mb-3">
          <i className="bi bi-info-circle me-1"></i>
          {formula === 'top4'
            ? 'Rang 1 & Rang 4 contre Rang 2 & Rang 3'
            : '2 meilleurs gauchers & 2 meilleurs droitiers'
          }
        </p>

        {/* Match card */}
        {finalMatch && (
          <>
            <div className={`final-match-card mb-4 ${isValidated ? 'final-match-validated' : ''}`}>

              {/* Teams */}
              <div className="final-teams-header">
                <div className={`final-team ${winner === 1 ? 'final-team-winner' : winner !== null ? 'final-team-loser' : ''}`}>
                  <div className="final-team-badge">Équipe 1</div>
                  {finalMatch.team1.map(id => {
                    const p = getPlayerById(id);
                    return (
                      <div key={id} className="final-player-name">
                        <i className={`bi bi-person-fill ${p?.position === 'left' ? 'text-primary' : 'text-danger'}`}></i>
                        {p?.name}
                        <span className="final-player-pos">{p?.position === 'left' ? 'G' : 'D'}</span>
                      </div>
                    );
                  })}
                  {winner === 1 && (
                    <div className="final-winner-badge">
                      <i className="bi bi-trophy-fill me-1"></i>Vainqueur
                    </div>
                  )}
                </div>

                <div className="final-vs">
                  {isValidated && finalMatch.detailedScore?.sets ? (
                    <div className="final-score-center">
                      {finalMatch.detailedScore.sets.map((set, i) => (
                        <div key={i} className="final-score-set-row">
                          <span className={`final-score-val ${set.team1 > set.team2 ? 'final-score-winner' : ''}`}>{set.team1}</span>
                          <span className="final-score-sep">-</span>
                          <span className={`final-score-val ${set.team2 > set.team1 ? 'final-score-winner' : ''}`}>{set.team2}</span>
                          {set.tiebreak && (
                            <span className="final-score-tb">({set.tiebreak.team1}-{set.tiebreak.team2})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : 'VS'}
                </div>

                <div className={`final-team ${winner === 2 ? 'final-team-winner' : winner !== null ? 'final-team-loser' : ''}`}>
                  <div className="final-team-badge">Équipe 2</div>
                  {finalMatch.team2.map(id => {
                    const p = getPlayerById(id);
                    return (
                      <div key={id} className="final-player-name">
                        <i className={`bi bi-person-fill ${p?.position === 'left' ? 'text-primary' : 'text-danger'}`}></i>
                        {p?.name}
                        <span className="final-player-pos">{p?.position === 'left' ? 'G' : 'D'}</span>
                      </div>
                    );
                  })}
                  {winner === 2 && (
                    <div className="final-winner-badge">
                      <i className="bi bi-trophy-fill me-1"></i>Vainqueur
                    </div>
                  )}
                </div>
              </div>

              {/* Score table (hidden after validation) */}
              {!isValidated && (
                <FinalScoreTable
                  finalMatch={finalMatch}
                  config={config}
                  onScoreChange={handleScoreInput}
                />
              )}


              {/* Validate / Edit button */}
              <div className="text-center mt-3">
                {isValidated ? (
                  <button
                    type="button"
                    className="btn btn-outline-light btn-sm"
                    onClick={unvalidateFinal}
                  >
                    <i className="bi bi-pencil-fill me-1"></i>Modifier
                  </button>
                ) : (
                  <button
                    type="button"
                    className="btn btn-generate"
                    onClick={validateFinal}
                    disabled={!isComplete}
                    title={!isComplete ? 'Complétez le score pour valider' : 'Valider la finale'}
                  >
                    <i className="bi bi-check-circle me-1"></i>Valider la finale
                  </button>
                )}
              </div>
            </div>

          </>
        )}
      </div>
    </div>
  );
};

// Score entry table
const FinalScoreTable = ({ finalMatch, config, onScoreChange }) => {
  const maxSets = Math.min((config.setsToWin || 1) * 2 - 1, 3);
  const maxGames = config.gamesPerSet || 6;

  return (
    <table className="table table-sm table-bordered mt-3 mb-0">
      <thead>
        <tr>
          <th>Équipe</th>
          {[...Array(maxSets)].map((_, i) => (
            <th key={i} className="text-center">SET {i + 1}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[1, 2].map(team => {
          const isWinner = finalMatch.detailedScore.isComplete() &&
            finalMatch.detailedScore.winner === (team === 1 ? 0 : 1);
          const isLoser = finalMatch.detailedScore.isComplete() &&
            !finalMatch.detailedScore.isDraw() && !isWinner;
          return (
            <tr key={team} className={isWinner ? 'final-row-winner' : isLoser ? 'final-row-loser' : ''}>
              <td><strong>Équipe {team}</strong></td>
              {[...Array(maxSets)].map((_, setIndex) => {
                const set = finalMatch.detailedScore.sets[setIndex];
                const gamesValue = set ? (team === 1 ? set.team1 : set.team2) : '';
                const tiebreakValue = set?.tiebreak
                  ? (team === 1 ? set.tiebreak.team1 : set.tiebreak.team2)
                  : '';
                const needsTiebreak = set && (
                  (set.team1 === maxGames + 1 && set.team2 === maxGames) ||
                  (set.team2 === maxGames + 1 && set.team1 === maxGames)
                );
                return (
                  <td key={setIndex} className="text-center">
                    <div className="d-flex flex-column gap-1 align-items-center">
                      <input
                        type="number"
                        className="form-control form-control-sm text-center final-score-input"
                        min="0"
                        max="20"
                        value={gamesValue}
                        placeholder="-"
                        onChange={(e) => onScoreChange(setIndex, team, e.target.value, false)}
                      />
                      {needsTiebreak && (
                        <div className="d-flex align-items-center gap-1">
                          <span className="small text-muted">TB:</span>
                          <input
                            type="number"
                            className="form-control form-control-sm text-center final-tiebreak-input"
                            min="0"
                            max="30"
                            value={tiebreakValue}
                            placeholder="-"
                            onChange={(e) => onScoreChange(setIndex, team, e.target.value, true)}
                          />
                        </div>
                      )}
                    </div>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default FinalSection;
